/**
 * Unified plan configuration for the entire application
 * This file is the single source of truth for all subscription plans
 */

export interface PlanFeature {
    key: string;
    translationKey: string;
}

export interface PlanConfig {
    id: string;
    name: string;
    translationKey: string;
    description: string;
    descriptionTranslationKey: string;
    price: number; // Price in cents (EUR)
    currency: string;
    interval: 'month' | 'year';
    monthlyImageLimit: number;
    tier: 'trial' | 'starter' | 'pro' | 'platinum';
    features: PlanFeature[];
    stripePriceId?: string;
    popular?: boolean;
    isActive: boolean;
    sortOrder: number;
    // Trial-specific fields
    trialDurationDays?: number;
    requiresPaymentMethod?: boolean;
}

/**
 * Central plan configuration - single source of truth
 */
export const PLANS: PlanConfig[] = [
    {
        id: 'trial',
        name: 'Trial',
        translationKey: 'plans.trial.name',
        description: 'Perfect for getting started - no payment required',
        descriptionTranslationKey: 'plans.trial.description',
        price: 0,
        currency: 'EUR',
        interval: 'month',
        monthlyImageLimit: 5,
        tier: 'trial',
        trialDurationDays: 14,
        requiresPaymentMethod: false,
        isActive: true,
        sortOrder: 0,
        features: [
            { key: 'trial_period', translationKey: 'plans.trial.features.period' },
            { key: 'trial_images', translationKey: 'plans.trial.features.images' },
            { key: 'community_support', translationKey: 'plans.trial.features.support' },
            { key: 'no_payment_required', translationKey: 'plans.trial.features.noPayment' },
        ],
    },
    {
        id: 'starter',
        name: 'Starter',
        translationKey: 'plans.starter.name',
        description: 'Perfect for small families getting started',
        descriptionTranslationKey: 'plans.starter.description',
        price: 100, // 1.00€/month
        currency: 'EUR',
        interval: 'month',
        monthlyImageLimit: 10,
        tier: 'starter',
        requiresPaymentMethod: true,
        isActive: true,
        sortOrder: 1,
        popular: false,
        features: [
            { key: 'images_monthly', translationKey: 'plans.starter.features.images' },
            { key: 'high_quality', translationKey: 'plans.starter.features.quality' },
            { key: 'email_support', translationKey: 'plans.starter.features.support' },
            { key: 'customization', translationKey: 'plans.starter.features.customization' },
            { key: 'sharing', translationKey: 'plans.starter.features.sharing' },
        ],
    },
    {
        id: 'pro',
        name: 'Pro',
        translationKey: 'plans.pro.name',
        description: 'For families who need more image generations',
        descriptionTranslationKey: 'plans.pro.description',
        price: 499, // 4.99€/month
        currency: 'EUR',
        interval: 'month',
        monthlyImageLimit: 60,
        tier: 'pro',
        requiresPaymentMethod: true,
        isActive: true,
        sortOrder: 2,
        popular: true,
        features: [
            { key: 'images_monthly', translationKey: 'plans.pro.features.images' },
            { key: 'high_quality', translationKey: 'plans.pro.features.quality' },
            { key: 'priority_support', translationKey: 'plans.pro.features.support' },
            { key: 'customization', translationKey: 'plans.pro.features.customization' },
            { key: 'sharing', translationKey: 'plans.pro.features.sharing' },
            { key: 'priority_generation', translationKey: 'plans.pro.features.priority' },
        ],
    },
    {
        id: 'platinum',
        name: 'Platinum',
        translationKey: 'plans.platinum.name',
        description: 'For power users who want unlimited creativity',
        descriptionTranslationKey: 'plans.platinum.description',
        price: 999, // 9.99€/month
        currency: 'EUR',
        interval: 'month',
        monthlyImageLimit: 150,
        tier: 'platinum',
        requiresPaymentMethod: true,
        isActive: true,
        sortOrder: 3,
        popular: false,
        features: [
            { key: 'images_monthly', translationKey: 'plans.platinum.features.images' },
            { key: 'highest_quality', translationKey: 'plans.platinum.features.quality' },
            { key: 'priority_support', translationKey: 'plans.platinum.features.support' },
            { key: 'customization', translationKey: 'plans.platinum.features.customization' },
            { key: 'sharing', translationKey: 'plans.platinum.features.sharing' },
            { key: 'priority_generation', translationKey: 'plans.platinum.features.priority' },
            { key: 'early_access', translationKey: 'plans.platinum.features.earlyAccess' },
            { key: 'advanced_customization', translationKey: 'plans.platinum.features.customization' },
            { key: 'newest_models', translationKey: 'plans.platinum.features.models' },
        ],
    },
];

/**
 * Helper functions for plan management
 */
export const getPlanById = (id: string): PlanConfig | undefined => {
    return PLANS.find(plan => plan.id === id);
};

export const getPlanByTier = (tier: string): PlanConfig | undefined => {
    return PLANS.find(plan => plan.tier === tier);
};

export const getTrialPlan = (): PlanConfig => {
    const trialPlan = PLANS.find(plan => plan.tier === 'trial');
    if (!trialPlan) {
        throw new Error('Trial plan not found in configuration');
    }
    return trialPlan;
};

export const getPaidPlans = (): PlanConfig[] => {
    return PLANS.filter(plan => plan.tier !== 'trial' && plan.isActive);
};

export const getAllActivePlans = (): PlanConfig[] => {
    return PLANS.filter(plan => plan.isActive).sort((a, b) => a.sortOrder - b.sortOrder);
};

/**
 * Plan validation helpers
 */
export const isValidPlanId = (planId: string): boolean => {
    return PLANS.some(plan => plan.id === planId);
};

export const isTrialPlan = (planId: string): boolean => {
    const plan = getPlanById(planId);
    return plan?.tier === 'trial';
};

export const isPaidPlan = (planId: string): boolean => {
    const plan = getPlanById(planId);
    return plan?.tier !== 'trial' && !!plan;
};

/**
 * Stripe integration helpers
 */
export const getPlanByStripePriceId = (stripePriceId: string): PlanConfig | undefined => {
    return PLANS.find(plan => plan.stripePriceId === stripePriceId);
};

export const getStripePriceIds = (): string[] => {
    return PLANS.filter(plan => plan.stripePriceId).map(plan => plan.stripePriceId!);
};

/**
 * Environment-based configuration
 * These should be set via environment variables for different environments
 */
export const getPlansWithEnvironmentData = (): PlanConfig[] => {
    return PLANS.map(plan => ({
        ...plan,
        stripePriceId: getStripePriceIdForPlan(plan.id),
    }));
};

function getStripePriceIdForPlan(planId: string): string | undefined {
    switch (planId) {
        case 'starter':
            return process.env.STRIPE_STARTER_MONTHLY_PRICE_ID;
        case 'pro':
            return process.env.STRIPE_PRO_MONTHLY_PRICE_ID;
        case 'platinum':
            return process.env.STRIPE_PLATINUM_MONTHLY_PRICE_ID;
        default:
            return undefined;
    }
}

/**
 * Environment Variables Required:
 *
 * STRIPE_STARTER_MONTHLY_PRICE_ID=price_1starter...
 * STRIPE_PRO_MONTHLY_PRICE_ID=price_1pro...
 * STRIPE_PLATINUM_MONTHLY_PRICE_ID=price_1platinum...
 */

/**
 * Legacy compatibility - maps old tier names to new ones
 */
export const LEGACY_TIER_MAPPING = {
    'free': 'trial',
    'premium': 'pro',
    'ultra': 'platinum',
} as const;

export const mapLegacyTier = (legacyTier: string): string => {
    return LEGACY_TIER_MAPPING[legacyTier as keyof typeof LEGACY_TIER_MAPPING] || legacyTier;
};
